import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  LinearProgress,
  Chip,
  Alert,
} from '@mui/material';
import seatService from '../../services/api/seatService';

// Lightweight seats card for the school/group detail pages.
//   limit (assignedSeats): null = unlimited, else a number — authored in Admin.
//   used: in-use count, read live from Identity (null if unavailable -> bar hidden).
export default function SeatCard({ entityType, entityId, assignedSeats, canEdit = true }) {
  const isSchool = entityType === 'school';

  const [limit, setLimit] = useState(
    assignedSeats === undefined ? null : assignedSeats
  );
  const [used, setUsed] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draftUnlimited, setDraftUnlimited] = useState(limit === null);
  const [draftValue, setDraftValue] = useState(limit === null ? '' : String(limit));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadUsage = useCallback(() => {
    if (!entityId) return;
    const call = isSchool ? seatService.getSchoolUsage : seatService.getGroupUsage;
    call(entityId).then((value) => setUsed(value));
  }, [entityId, isSchool]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  // assignedSeats is often populated after an async load on the detail pages, so
  // adopt the prop when it arrives — but never clobber an in-progress edit/save.
  useEffect(() => {
    if (editing || saving) return;
    setLimit(assignedSeats === undefined ? null : assignedSeats);
  }, [assignedSeats, editing, saving]);

  const startEdit = () => {
    setError(null);
    setDraftUnlimited(limit === null);
    setDraftValue(limit === null ? '' : String(limit));
    setEditing(true);
  };

  const save = async () => {
    const newLimit = draftUnlimited
      ? null
      : Math.max(0, parseInt(draftValue, 10) || 0);
    setSaving(true);
    setError(null);
    try {
      const setter = isSchool ? seatService.setSchoolLimit : seatService.setGroupLimit;
      const res = await setter(entityId, newLimit);
      if (res && res.success === false) throw new Error('rejected');
      setLimit(newLimit);
      setEditing(false);
      loadUsage();
    } catch (e) {
      setError('Could not update the seat limit. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const unlimited = limit === null;
  const ratio = !unlimited && limit > 0 && used != null ? used / limit : 0;
  const barColor = ratio >= 1 ? 'error' : ratio >= 0.8 ? 'warning' : 'success';

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 600 }}>Seats</Typography>
        {canEdit && !editing && (
          <Button variant='text' size="small" sx={{color: '#007A7A', textTransform: 'none', padding: 0, minWidth: '0'}} onClick={startEdit}>
            Edit
          </Button>
        )}
      </Box>

      {editing ? (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={draftUnlimited}
                  onChange={(e) => setDraftUnlimited(e.target.checked)}
                />
              }
              label="Unlimited"
            />
            <TextField
              size="small"
              type="number"
              label="Assigned seats"
              value={draftUnlimited ? '' : draftValue}
              onChange={(e) => setDraftValue(e.target.value)}
              disabled={draftUnlimited}
              inputProps={{ min: 0 }}
              sx={{ width: 140 }}
            />
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button size="small" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={save} disabled={saving}>
                Save
              </Button>
            </Box>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Limit on staff accounts that can be assigned to this {isSchool ? 'school' : 'group'}.
          </Typography>
        </Box>
      ) : unlimited ? (
        <Box
          sx={{
            mt: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {used != null ? `${used} assigned · ` : ''}no limit set
          </Typography>
          <Chip label="Unlimited" size="small" />
        </Box>
      ) : (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              {used != null ? used : '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              of {limit} used
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {used != null
                ? used >= limit
                  ? 'full'
                  : `${limit - used} remaining`
                : ''}
            </Typography>
          </Box>
          {used != null && (
            <LinearProgress
              variant="determinate"
              value={Math.min(100, ratio * 100)}
              color={barColor}
              sx={{ mt: 1, height: 6, borderRadius: 3 }}
            />
          )}
        </Box>
      )}
    </Paper>
  );
}
